import React, { useState, useEffect, useRef, useCallback } from 'react';

import RagPipeline from './RagPipeline';

// 전역 공통 데이터 컬렉션 식별자 (백엔드 COMMON_COLLECTION_NAME과 일치해야 함)
const COMMON_COLLECTION = '__common__';

// [Helper] 긴 텍스트 접기/펼치기 컴포넌트
const ExpandableText = ({ text, maxLength = 50 }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text) return <span className="text-gray-400">-</span>;
  if (text.length <= maxLength) return <span>{text}</span>;

  return (
    <div>
      <p className="break-all whitespace-pre-wrap">
        {isExpanded ? text : `${text.slice(0, maxLength)}...`}
      </p>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="text-blue-500 hover:text-blue-700 text-xs mt-1 font-bold"
      >
        {isExpanded ? '접기' : '더보기'}
      </button>
    </div>
  );
};

const AdminDashboard = ({ token }) => {
  const authFetch = (url, options = {}) => fetch(url, {
    ...options,
    headers: { ...options.headers, 'Authorization': `Bearer ${token}` },
  });
  const [activeTab, setActiveTab] = useState('dictionary'); // 'dictionary' | 'prompt' | 'config' | 'feedback' | 'learn' | 'collection'
  
  // --- Dictionary State ---
  const [dictData, setDictData] = useState([]);
  const [selectedTermId, setSelectedTermId] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [variantsList, setVariantsList] = useState([]);

  // --- Modal State ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newCanonical, setNewCanonical] = useState('');
  const [newVariants, setNewVariants] = useState('');

  // --- Prompt State ---
  const [prompts, setPrompts] = useState({
    main_system_prompt: '',
    injection_guard_prompt: '',
    rerank_prompt: '',
    query_expansion_prompt: '',
    user_prompt_template: ''
  });
  const [activePromptKey, setActivePromptKey] = useState('main_system_prompt');

  // --- RAG Config State ---
  const [ragConfig, setRagConfig] = useState({
    top_k: 5,
    threshold: 1.5,
    temperature: 0.0
  });

  // --- Feedback State ---
  const [feedbackList, setFeedbackList] = useState([]);

  // --- Learn State (New) ---
  const [learnText, setLearnText] = useState('');
  const [learnQuestion, setLearnQuestion] = useState(''); // 질문 입력 필드 추가
  const [learnKeywords, setLearnKeywords] = useState(''); // 키워드 입력 필드 추가
  const [learnSource, setLearnSource] = useState('관리자 입력');
  const [learnCategory, setLearnCategory] = useState('General');

  // --- Collection State ---
  const [collectionData, setCollectionData] = useState({ active: '', collections: [] });
  const [createColName, setCreateColName] = useState('');
  const [createColDesc, setCreateColDesc] = useState('');
  const [isCreateColOpen, setIsCreateColOpen] = useState(false);
  const [embedTarget, setEmbedTarget] = useState('');
  const [embedCategory, setEmbedCategory] = useState('');
  const [embedStrategy, setEmbedStrategy] = useState('paragraph');
  const [embedFile, setEmbedFile] = useState(null);
  const [embedKeywords, setEmbedKeywords] = useState('');
  const [embedLoading, setEmbedLoading] = useState(false);
  const [embedProgress, setEmbedProgress] = useState(0);
  const [embedResult, setEmbedResult] = useState(null);
  const [embeddedFiles, setEmbeddedFiles] = useState([]);
  const [expandedFile, setExpandedFile] = useState(null);
  const [fileChunksData, setFileChunksData] = useState({ chunks: [], total: 0, page: 1, pages: 1 });
  const [fileChunksLoading, setFileChunksLoading] = useState(false);

  const [isDragOver, setIsDragOver] = useState(false);

  // --- Parse Preview State ---
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [previewPage, setPreviewPage] = useState(1);
  const [expandedChunks, setExpandedChunks] = useState(new Set());
  const previewAbortRef = useRef(null);
  const PREVIEW_PAGE_SIZE = 10;

  // --- Common Data Tab State (전역 공통 데이터 관리) ---
  const [commonStrategy, setCommonStrategy] = useState('paragraph');
  const [commonFile, setCommonFile] = useState(null);
  const [commonLoading, setCommonLoading] = useState(false);
  const [commonProgress, setCommonProgress] = useState(0);
  const [commonResult, setCommonResult] = useState(null);
  const [commonFiles, setCommonFiles] = useState([]);

  // --- Test Tab State ---
  const [testCollection, setTestCollection] = useState('');
  const [testQuery, setTestQuery] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [playgroundTopK, setPlaygroundTopK] = useState(5);
  const [playgroundTemperature, setPlaygroundTemperature] = useState(0.0);
  const [playgroundMaxTokens, setPlaygroundMaxTokens] = useState(1000);
  const [docBrowserCollection, setDocBrowserCollection] = useState('');
  const [docBrowserKeyword, setDocBrowserKeyword] = useState('');
  const [docBrowserCategory, setDocBrowserCategory] = useState('');
  const [docBrowserPage, setDocBrowserPage] = useState(1);
  const [docBrowserData, setDocBrowserData] = useState({ documents: [], total: 0, pages: 1 });
  const [docBrowserLoading, setDocBrowserLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ categories: [], keywords: [] });

  // --- Education State ---
  const [eduCollection, setEduCollection] = useState('');
  const [educations, setEducations] = useState([]);
  const [newEduName, setNewEduName] = useState('');
  const [newEduDesc, setNewEduDesc] = useState('');
  const [embedEducations, setEmbedEducations] = useState([]);

  // --- Theme State ---
  const [themeData, setThemeData] = useState({ greeting: '', primary_color: '#1A73E8', has_custom_logo: false, has_custom_profile: false });
  const [themeLogoPreview, setThemeLogoPreview] = useState(null);
  const [themeLogoFile, setThemeLogoFile] = useState(null);
  const [themeProfilePreview, setThemeProfilePreview] = useState(null);
  const [themeProfileFile, setThemeProfileFile] = useState(null);
  const [themeSaving, setThemeSaving] = useState(false);

  // --- Alert & Confirm Modal State ---
  const [alertState, setAlertState] = useState({ isOpen: false, message: '' });
  const [confirmState, setConfirmState] = useState({ isOpen: false, message: '', onConfirm: null });

  const showAlert = (message) => {
    setAlertState({ isOpen: true, message });
  };

  const closeAlert = () => {
    setAlertState({ isOpen: false, message: '' });
  };

  const showConfirm = (message, onConfirm) => {
    setConfirmState({ isOpen: true, message, onConfirm });
  };

  const closeConfirm = () => {
    setConfirmState({ isOpen: false, message: '', onConfirm: null });
  };

  const handleConfirmAction = () => {
    if (confirmState.onConfirm) {
      confirmState.onConfirm();
    }
    closeConfirm();
  };

  // 1. 초기 데이터 로드 (탭 변경 시)
  useEffect(() => {
    if (activeTab === 'dictionary') fetchDictionary();
    else if (activeTab === 'prompt') fetchPrompt();
    else if (activeTab === 'config') fetchConfig();
    else if (activeTab === 'pipeline') fetchConfig();
    else if (activeTab === 'feedback') fetchFeedback();
    else if (activeTab === 'collection') fetchCollections();
    else if (activeTab === 'education') fetchCollections();
    else if (activeTab === 'test') fetchCollections();
    else if (activeTab === 'common') fetchCommonFiles();
    else if (activeTab === 'theme') fetchTheme();
  }, [activeTab]);

  // 선택된 컬렉션이 바뀌면 교육 목록을 다시 로드
  useEffect(() => {
    if (eduCollection) fetchEducations(eduCollection);
  }, [eduCollection]);

  // 임베딩 대상 컬렉션이 바뀌면 교육 드롭다운 목록을 다시 로드
  useEffect(() => {
    fetchEmbedEducations(embedTarget);
  }, [embedTarget]);

  const fetchDictionary = () => {
    authFetch('/api/dictionary')
      .then(res => res.json())
      .then(data => setDictData(data))
      .catch(err => console.error(err));
  };

  const fetchPrompt = () => {
    authFetch('/api/prompts')
      .then(res => res.json())
      .then(data => setPrompts(data))
      .catch(err => console.error(err));
  };

  const fetchConfig = () => {
    authFetch('/api/config')
      .then(res => res.json())
      .then(data => setRagConfig(data))
      .catch(err => console.error(err));
  };

  const fetchFeedback = () => {
    authFetch('/api/feedback')
      .then(res => res.json())
      .then(data => setFeedbackList(data))
      .catch(err => console.error(err));
  };

  // --- Collection Handlers ---
  const fetchCollections = () => {
    authFetch('/api/collections')
      .then(res => res.json())
      .then(data => {
        setCollectionData(data);
        if (!embedTarget && data.collections.length > 0) {
          setEmbedTarget(data.collections[0].name);
          fetchEmbeddedFiles(data.collections[0].name);
        }
        if (data.active) {
          setTestCollection(prev => prev || data.active);
          setDocBrowserCollection(prev => prev || data.active);
          setEduCollection(prev => prev || data.active);
          fetchFilterOptions(data.active);
        }
      })
      .catch(err => console.error(err));
  };

  // --- Education Handlers ---
  const fetchEducations = (colName) => {
    if (!colName) return;
    authFetch(`/api/collections/${colName}/educations`)
      .then(res => res.json())
      .then(data => setEducations(Array.isArray(data) ? data : []))
      .catch(err => console.error(err));
  };

  const fetchEmbedEducations = (colName) => {
    if (!colName) { setEmbedEducations([]); return; }
    authFetch(`/api/collections/${colName}/educations`)
      .then(res => res.json())
      .then(data => setEmbedEducations(Array.isArray(data) ? data : []))
      .catch(err => console.error(err));
  };

  const handleCreateEducation = async () => {
    if (!eduCollection) return showAlert('대상 컬렉션을 선택해주세요.');
    if (!newEduName.trim()) return showAlert('교육명을 입력해주세요.');
    const res = await authFetch(`/api/collections/${eduCollection}/educations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newEduName.trim(), description: newEduDesc }),
    });
    const data = await res.json();
    if (res.ok) {
      showAlert(`'${newEduName.trim()}' 교육이 추가되었습니다.`);
      setNewEduName(''); setNewEduDesc('');
      fetchEducations(eduCollection);
      if (eduCollection === embedTarget) fetchEmbedEducations(embedTarget);
    } else {
      showAlert('추가 실패: ' + (data.detail || data.message));
    }
  };

  const handleDeleteEducation = async (id, name) => {
    showConfirm(`'${name}' 교육을 삭제하시겠습니까?\n(이미 임베딩된 문서는 그대로 유지됩니다)`, async () => {
      const res = await authFetch(`/api/educations/${id}`, { method: 'DELETE' });
      if (res.ok) {
        showAlert('삭제되었습니다.');
        fetchEducations(eduCollection);
        if (eduCollection === embedTarget) fetchEmbedEducations(embedTarget);
      } else {
        const d = await res.json();
        showAlert('실패: ' + (d.detail || d.message));
      }
    });
  };

  // 교육 순서 이동 (위/아래). 로컬 배열을 바꾸고 새 순서를 서버에 저장.
  const handleMoveEducation = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= educations.length) return;
    const reordered = [...educations];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setEducations(reordered); // 낙관적 갱신
    const res = await authFetch(`/api/collections/${eduCollection}/educations/reorder`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: reordered.map(e => e.id) }),
    });
    if (res.ok) {
      if (eduCollection === embedTarget) fetchEmbedEducations(embedTarget);
    } else {
      showAlert('순서 변경 실패');
      fetchEducations(eduCollection); // 롤백
    }
  };

  const fetchEmbeddedFiles = (colName) => {
    authFetch(`/api/collections/${colName}/files`)
      .then(res => res.json())
      .then(data => setEmbeddedFiles(data))
      .catch(err => console.error(err));
  };

  const handleCreateCollection = async () => {
    if (!createColName.trim()) return showAlert('컬렉션 이름을 입력해주세요.');
    if (!/^[a-zA-Z0-9_-]{3,63}$/.test(createColName))
      return showAlert('영문/숫자/언더스코어/하이픈 3~63자만 사용 가능합니다.');
    const res = await authFetch('/api/collections', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: createColName, description: createColDesc }),
    });
    const data = await res.json();
    if (res.ok) {
      showAlert(`'${createColName}' 컬렉션이 생성되었습니다.`);
      setIsCreateColOpen(false);
      setCreateColName(''); setCreateColDesc('');
      fetchCollections();
    } else {
      showAlert('생성 실패: ' + (data.detail || data.message));
    }
  };

  const handleActivateCollection = async (name) => {
    showConfirm(`'${name}'을 활성 컬렉션으로 전환하시겠습니까?\n(BM25 인덱스가 재구축됩니다)`, async () => {
      const res = await authFetch('/api/collections/activate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (res.ok) { showAlert(`'${name}'이 활성화되었습니다.`); fetchCollections(); }
      else { const d = await res.json(); showAlert('실패: ' + (d.detail || d.message)); }
    });
  };

  const handleDeleteCollection = async (name) => {
    showConfirm(`'${name}' 컬렉션과 모든 문서를 삭제하시겠습니까?`, async () => {
      const res = await authFetch(`/api/collections/${name}`, { method: 'DELETE' });
      if (res.ok) { showAlert(`'${name}'이 삭제되었습니다.`); fetchCollections(); }
      else { const d = await res.json(); showAlert('실패: ' + (d.detail || d.message)); }
    });
  };

  const fetchPreview = useCallback(async (file, strategy) => {
    if (!file) { setPreviewData(null); setPreviewError(null); return; }
    if (previewAbortRef.current) previewAbortRef.current.abort();
    const ctrl = new AbortController();
    previewAbortRef.current = ctrl;
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewPage(1);
    setExpandedChunks(new Set());
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await authFetch(
        `/api/parse-preview?strategy=${strategy}&category=General`,
        { method: 'POST', body: form, signal: ctrl.signal }
      );
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || '미리보기 실패'); }
      const data = await res.json();
      setPreviewData(data);
    } catch (e) {
      if (e.name !== 'AbortError') setPreviewError(e.message);
    } finally {
      setPreviewLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setTimeout(() => fetchPreview(embedFile, embedStrategy), 300);
    return () => clearTimeout(timer);
  }, [embedFile, embedStrategy]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEmbedFile = async () => {
    if (!embedFile) return showAlert('파일을 선택해주세요.');
    if (!embedTarget) return showAlert('대상 컬렉션을 선택해주세요.');
    if (!embedCategory) return showAlert('교육을 선택해주세요. (교육 관리 탭에서 먼저 등록)');
    setEmbedLoading(true);
    setEmbedResult(null);
    setEmbedProgress(0);
    try {
      const form = new FormData();
      form.append('file', embedFile);
      const res = await authFetch(
        `/api/collections/${embedTarget}/embed?strategy=${embedStrategy}&category=${encodeURIComponent(embedCategory)}&keywords=${encodeURIComponent(embedKeywords)}`,
        { method: 'POST', body: form }
      );
      if (!res.ok) {
        const text = await res.text();
        let data = {};
        try { data = JSON.parse(text); } catch (_) {}
        const msg = data.detail || data.message || (res.status === 413 ? '파일이 너무 큽니다 (최대 50MB)' : `서버 오류 (${res.status})`);
        setEmbedResult({ success: false, message: msg });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.progress !== undefined) setEmbedProgress(data.progress);
            if (data.done) {
              setEmbedProgress(100);
              setEmbedResult({ success: true, ...data });
              setEmbedFile(null);
              fetchEmbeddedFiles(embedTarget);
              fetchCollections();
            }
            if (data.error) setEmbedResult({ success: false, message: data.error });
          } catch (_) {}
        }
      }
    } catch (e) {
      setEmbedResult({ success: false, message: e.message });
    } finally {
      setEmbedLoading(false);
    }
  };

  const handleDeleteEmbeddedFile = (colName, filename) => {
    showConfirm(`'${filename}' 파일과 관련 청크를 삭제하시겠습니까?`, async () => {
      const res = await authFetch(`/api/collections/${colName}/files/${encodeURIComponent(filename)}`, { method: 'DELETE' });
      if (res.ok) { fetchEmbeddedFiles(colName); fetchCollections(); }
      else { const d = await res.json(); showAlert('실패: ' + (d.detail || d.message)); }
    });
  };

  const fetchFileChunks = async (colName, filename, page = 1) => {
    setFileChunksLoading(true);
    try {
      const res = await authFetch(`/api/collections/${colName}/files/${encodeURIComponent(filename)}/chunks?page=${page}&page_size=20`);
      if (res.ok) setFileChunksData(await res.json());
    } catch (e) { console.error(e); }
    finally { setFileChunksLoading(false); }
  };

  const handleToggleChunks = (filename, colName = embedTarget) => {
    if (expandedFile === filename) {
      setExpandedFile(null);
    } else {
      setExpandedFile(filename);
      setFileChunksData({ chunks: [], total: 0, page: 1, pages: 1 });
      fetchFileChunks(colName, filename, 1);
    }
  };

  const handleDeleteChunk = (chunkId, filename, colName = embedTarget) => {
    showConfirm('이 청크를 삭제하시겠습니까?', async () => {
      const res = await authFetch(`/api/collections/${colName}/chunks/${chunkId}`, { method: 'DELETE' });
      if (res.ok) {
        fetchFileChunks(colName, filename, fileChunksData.page);
        if (colName === COMMON_COLLECTION) fetchCommonFiles();
        else fetchEmbeddedFiles(colName);
      } else {
        const d = await res.json();
        showAlert('삭제 실패: ' + (d.detail || d.message));
      }
    });
  };

  // --- Common Data Tab Handlers (전역 공통 데이터) ---
  const fetchCommonFiles = () => {
    authFetch(`/api/collections/${COMMON_COLLECTION}/files`)
      .then(res => res.json())
      .then(data => setCommonFiles(Array.isArray(data) ? data : []))
      .catch(() => setCommonFiles([]));
  };

  const handleCommonEmbed = async () => {
    if (!commonFile) return showAlert('파일을 선택해주세요.');
    setCommonLoading(true);
    setCommonResult(null);
    setCommonProgress(0);
    try {
      const form = new FormData();
      form.append('file', commonFile);
      // 공통 데이터는 교육(category) 구분 없이 항상 참조되므로 category는 '공통'으로 고정한다.
      const res = await authFetch(
        `/api/collections/${COMMON_COLLECTION}/embed?strategy=${commonStrategy}&category=${encodeURIComponent('공통')}`,
        { method: 'POST', body: form }
      );
      if (!res.ok) {
        const text = await res.text();
        let data = {};
        try { data = JSON.parse(text); } catch (_) {}
        const msg = data.detail || data.message || (res.status === 413 ? '파일이 너무 큽니다 (최대 50MB)' : `서버 오류 (${res.status})`);
        setCommonResult({ success: false, message: msg });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.progress !== undefined) setCommonProgress(data.progress);
            if (data.done) {
              setCommonProgress(100);
              setCommonResult({ success: true, ...data });
              setCommonFile(null);
              fetchCommonFiles();
            }
            if (data.error) setCommonResult({ success: false, message: data.error });
          } catch (_) {}
        }
      }
    } catch (e) {
      setCommonResult({ success: false, message: e.message });
    } finally {
      setCommonLoading(false);
    }
  };

  const handleDeleteCommonFile = (filename) => {
    showConfirm(`'${filename}' 파일과 관련 청크를 삭제하시겠습니까?`, async () => {
      const res = await authFetch(`/api/collections/${COMMON_COLLECTION}/files/${encodeURIComponent(filename)}`, { method: 'DELETE' });
      if (res.ok) { fetchCommonFiles(); }
      else { const d = await res.json(); showAlert('실패: ' + (d.detail || d.message)); }
    });
  };

  // --- Test Tab Handlers ---
  const handleTestQuery = async () => {
    if (!testQuery.trim()) return showAlert('질문을 입력해주세요.');
    const col = testCollection || collectionData.active;
    if (!col) return showAlert('컬렉션을 선택해주세요.');
    setTestLoading(true);
    setTestResult(null);
    try {
      const res = await authFetch(`/api/collections/${col}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: testQuery, top_k: playgroundTopK, temperature: playgroundTemperature, max_tokens: playgroundMaxTokens }),
      });
      const data = await res.json();
      if (res.ok) setTestResult(data);
      else showAlert('테스트 실패: ' + (data.detail || data.message || ''));
    } catch (e) {
      showAlert('오류: ' + e.message);
    } finally {
      setTestLoading(false);
    }
  };

  const handleFetchDocBrowser = async (col, keyword, category, page) => {
    const target = col || docBrowserCollection || collectionData.active;
    if (!target) return;
    setDocBrowserLoading(true);
    const params = new URLSearchParams({ page: page || 1, keyword: keyword ?? docBrowserKeyword, category: category ?? docBrowserCategory });
    try {
      const res = await authFetch(`/api/collections/${target}/documents?${params}`);
      const data = await res.json();
      if (res.ok) setDocBrowserData(data);
    } catch (e) {
      console.error(e);
    } finally {
      setDocBrowserLoading(false);
    }
  };

  const fetchFilterOptions = async (col) => {
    const target = col || docBrowserCollection || collectionData.active;
    if (!target) return;
    try {
      const res = await authFetch(`/api/collections/${target}/filter-options`);
      if (res.ok) {
        const data = await res.json();
        setFilterOptions(data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchTheme = () => {
    fetch('/api/theme')
      .then(r => r.json())
      .then(data => {
        setThemeData(data);
        if (data.has_custom_logo) setThemeLogoPreview('/api/theme/logo?t=' + Date.now());
        else setThemeLogoPreview(null);
        if (data.has_custom_profile) setThemeProfilePreview('/api/theme/profile?t=' + Date.now());
        else setThemeProfilePreview(null);
      })
      .catch(console.error);
  };

  const handleSaveTheme = async () => {
    setThemeSaving(true);
    try {
      const res = await authFetch('/api/theme', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ greeting: themeData.greeting, primary_color: themeData.primary_color }),
      });
      if (res.ok) {
        if (themeLogoFile) {
          const form = new FormData();
          form.append('file', themeLogoFile);
          await authFetch('/api/theme/logo', { method: 'POST', body: form });
        }
        if (themeProfileFile) {
          const form = new FormData();
          form.append('file', themeProfileFile);
          await authFetch('/api/theme/profile', { method: 'POST', body: form });
        }
        showAlert('테마가 저장되었습니다. 새로고침 시 적용됩니다.');
        setThemeLogoFile(null);
        setThemeProfileFile(null);
        fetchTheme();
      } else {
        const d = await res.json();
        showAlert('저장 실패: ' + (d.detail || ''));
      }
    } finally {
      setThemeSaving(false);
    }
  };

  const handleDeleteLogo = async () => {
    showConfirm('로고를 기본값으로 되돌리겠습니까?', async () => {
      const res = await authFetch('/api/theme/logo', { method: 'DELETE' });
      if (res.ok) { setThemeLogoPreview(null); setThemeLogoFile(null); fetchTheme(); }
    });
  };

  const handleDeleteProfile = async () => {
    showConfirm('챗봇 프로필 이미지를 기본 아이콘으로 되돌리겠습니까?', async () => {
      const res = await authFetch('/api/theme/profile', { method: 'DELETE' });
      if (res.ok) { setThemeProfilePreview(null); setThemeProfileFile(null); fetchTheme(); }
    });
  };

  // --- Dictionary Handlers ---
  const handleSearch = () => {
    if (!selectedTermId) return showAlert("동의어명을 선택해주세요.");
    const item = dictData.find(d => d.term_id === selectedTermId);
    if (item) {
      setSelectedItem(item);
      setVariantsList(item.variants.split('|').map(v => v.trim()).filter(v => v));
    } else {
      setSelectedItem(null);
      setVariantsList([]);
    }
  };

  const handleVariantChange = (index, value) => {
    const newList = [...variantsList];
    newList[index] = value;
    setVariantsList(newList);
  };

  const handleDelete = (index) => {
    setVariantsList(variantsList.filter((_, i) => i !== index));
  };

  const handleAddRow = () => {
    setVariantsList([...variantsList, '']);
  };

  const handleDictSave = async () => {
    if (!selectedItem) return;
    const variantsStr = variantsList.map(v => v.trim()).filter(v => v).join('|');
    const updatedData = dictData.map(item => 
      item.term_id === selectedItem.term_id ? { ...item, variants: variantsStr } : item
    );
    
    await authFetch('/api/dictionary', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedData)
    });
    showAlert('동의어 사전이 업데이트되었습니다!');
    setDictData(updatedData);
    setSelectedItem(updatedData.find(d => d.term_id === selectedItem.term_id));
  };

  const handleAddNewTerm = async () => {
    if (!newCanonical.trim()) return showAlert("동의어명을 입력해주세요.");

    // 1. ID 자동 생성 (최대값 + 1)
    const maxId = dictData.reduce((max, item) => {
      const num = parseInt(item.term_id.replace('D', ''), 10);
      return num > max ? num : max;
    }, 0);
    const newId = `D${String(maxId + 1).padStart(4, '0')}`;

    // 2. 새 아이템 생성
    const newItem = {
      term_id: newId,
      canonical_term: newCanonical.trim(),
      variants: newVariants.trim(), // 사용자가 입력한 그대로 저장 (파이프 | 로 구분 권장)
      freq_count: "0"
    };

    // 3. 데이터 업데이트 및 저장
    const updatedData = [...dictData, newItem];
    
    await authFetch('/api/dictionary', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedData)
    });

    showAlert(`새로운 동의어 '${newItem.canonical_term}'(이)가 추가되었습니다.`);
    setDictData(updatedData);
    
    // 모달 닫기 및 초기화
    setIsModalOpen(false);
    setNewCanonical('');
    setNewVariants('');
    
    // 추가된 항목 바로 선택
    setSelectedTermId(newId);
    setSelectedItem(newItem);
    setVariantsList(newItem.variants.split('|').filter(v => v.trim()));
  };

  // --- Prompt Handlers ---
  const handlePromptSave = async () => {
    await authFetch('/api/prompts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(prompts)
    });
    showAlert('프롬프트가 저장되었습니다.');
  };

  // --- Config Handlers ---
  const handleConfigSave = async () => {
    await authFetch('/api/config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(ragConfig)
    });
    showAlert('RAG 설정이 저장되었습니다.');
  };

  const handleDeleteFeedback = async (id) => {
    showConfirm("이 피드백 로그를 삭제하시겠습니까?", async () => {
      await authFetch(`/api/feedback/${id}`, { method: 'DELETE' });
      setFeedbackList(prev => prev.filter(item => item.id !== id));
    });
  };

  // --- Learn Handlers (New) ---
  const handleLearn = async () => {
    if (!learnText.trim()) return showAlert("학습할 내용을 입력해주세요.");

    try {
      const res = await authFetch('/api/learn', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: learnText,
          source: learnSource,
          category: learnCategory
        })
      });
      const data = await res.json();
      
      if (data.status === 'success') {
        showAlert('새로운 지식이 성공적으로 학습되었습니다!');
        setLearnText(''); // 입력창 초기화
      } else {
        showAlert('학습 실패: ' + data.message);
      }
    } catch (e) {
      showAlert('오류 발생: ' + e.message);
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full bg-bg-main p-6 overflow-hidden">
      {/* 탭 네비게이션 */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {['dictionary', 'prompt', 'config', 'pipeline', 'feedback', 'learn', 'collection', 'education', 'common', 'test', 'theme'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-3 font-bold text-sm transition-colors rounded-t-lg capitalize ${
              activeTab === tab
                ? 'bg-white text-main-point border-t border-l border-r border-gray-200 shadow-sm -mb-px'
                : 'text-gray-500 hover:text-gray-700 hover:bg-sub-element'
            }`}
          >
            {tab === 'dictionary' ? '동의어 사전' :
             tab === 'prompt' ? '프롬프트' :
             tab === 'config' ? 'RAG 설정' :
             tab === 'feedback' ? '피드백' :
             tab === 'learn' ? '지식 학습' :
             tab === 'collection' ? '컬렉션 관리' :
             tab === 'education' ? '교육 관리' :
             tab === 'pipeline' ? 'RAG 파이프라인' :
             tab === 'common' ? '공통 데이터 관리' :
             tab === 'test' ? '플레이그라운드' : '테마'}
          </button>
        ))}
      </div>

      {/* 1. 동의어 관리 */}
      {activeTab === 'dictionary' && (
        <>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-4 flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-bold text-gray-700 mb-1">동의어명 (Canonical Term)</label>
              <select 
                value={selectedTermId} 
                onChange={(e) => setSelectedTermId(e.target.value)}
                className="block w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="">-- 선택하세요 --</option>
                {dictData.map(item => (
                  <option key={item.term_id} value={item.term_id}>{item.canonical_term}</option>
                ))}
              </select>
            </div>
            <button onClick={handleSearch} className="px-6 py-2.5 bg-gray-700 text-white rounded-md hover:bg-gray-800">조회</button>
            <button 
              onClick={() => setIsModalOpen(true)} 
              className="px-4 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-bold"
            >
              + 추가
            </button>
          </div>

          {/* [추가] 새 동의어 추가 모달 */}
          {isModalOpen && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
                <h3 className="text-lg font-bold mb-4 text-gray-800">새 동의어 추가</h3>
                
                <div className="mb-4">
                  <label className="block text-sm font-bold mb-1 text-gray-700">동의어명 (Canonical Term)</label>
                  <input 
                    type="text" 
                    value={newCanonical}
                    onChange={(e) => setNewCanonical(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 mb-4 focus:outline-none focus:border-main-point"
                    placeholder="예: 결제"
                  />
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-bold mb-1 text-gray-700">유의어 (Variants)</label>
                  <textarea 
                    value={newVariants}
                    onChange={(e) => setNewVariants(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 mb-4 focus:outline-none focus:border-main-point h-24 resize-none"
                    placeholder="유의어1|유의어2|유의어3 (파이프 | 로 구분)"
                  />
                </div>
                
                <div className="flex justify-end gap-2">
                  <button 
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg text-sm"
                  >
                    취소
                  </button>
                  <button 
                    onClick={handleAddNewTerm}
                    className="px-4 py-2 bg-main-point text-white rounded-lg text-sm font-bold hover:bg-blue-700"
                  >
                    저장
                  </button>
                </div>
              </div>
            </div>
          )}

          {selectedItem && (
            <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col overflow-hidden">
              <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                <h3 className="font-bold text-lg text-gray-800">'{selectedItem.canonical_term}' 목록</h3>
                <button onClick={handleAddRow} className="text-sm bg-blue-100 text-blue-700 px-3 py-1.5 rounded hover:bg-blue-200">+ 행 추가</button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <table className="w-full text-sm text-left text-gray-500">
                  <thead className="text-xs text-gray-700 uppercase bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 border w-16 text-center">순번</th>
                      <th className="px-4 py-3 border">동의어</th>
                      <th className="px-4 py-3 border w-24 text-center">관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variantsList.map((variant, index) => (
                      <tr key={index} className="bg-white border-b hover:bg-gray-50">
                        <td className="px-4 py-2 border text-center">{index + 1}</td>
                        <td className="px-4 py-2 border">
                          <input type="text" value={variant} onChange={(e) => handleVariantChange(index, e.target.value)} className="w-full p-2 border border-gray-300 rounded outline-none" />
                        </td>
                        <td className="px-4 py-2 border text-center">
                          <button onClick={() => handleDelete(index)} className="text-red-500 hover:text-red-700">삭제</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="p-4 border-t bg-gray-50 text-right">
                <button onClick={handleDictSave} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">저장</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* 2. 프롬프트 관리 */}
      {activeTab === 'prompt' && (
        <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-bold text-lg text-gray-800">프롬프트 관리</h3>
            <p className="text-sm text-gray-500 mt-1">RAG 파이프라인에서 사용되는 모든 시스템 프롬프트를 관리합니다.</p>
          </div>

          {/* 프롬프트 서브탭 */}
          <div className="flex border-b bg-white px-4 pt-2 gap-1">
            {[
              { key: 'main_system_prompt', label: '메인 시스템' },
              { key: 'injection_guard_prompt', label: '인젝션 가드' },
              { key: 'rerank_prompt', label: '리랭킹' },
              { key: 'query_expansion_prompt', label: '쿼리 확장' },
              { key: 'user_prompt_template', label: '유저 프롬프트' },
            ].map(item => (
              <button
                key={item.key}
                onClick={() => setActivePromptKey(item.key)}
                className={`px-4 py-2 text-sm font-bold rounded-t-lg transition-colors ${
                  activePromptKey === item.key
                    ? 'bg-gray-100 text-blue-600 border-t border-l border-r border-gray-200 -mb-px'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* 프롬프트 설명 */}
          <div className="px-6 pt-4">
            <p className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
              {activePromptKey === 'main_system_prompt' && '챗봇의 역할, 말투, 답변 규칙을 정의하는 메인 시스템 프롬프트입니다.'}
              {activePromptKey === 'injection_guard_prompt' && '프롬프트 인젝션·탈옥·환각 압박을 방어하는 보안 가드입니다. 메인 시스템 프롬프트 뒤에 항상 덧붙어 적용되며(채팅·플레이그라운드 공통), 비워서 저장하면 코드 기본값으로 복원됩니다.'}
              {activePromptKey === 'rerank_prompt' && '검색된 문서의 관련도를 재평가하는 GPT-4o-mini 리랭킹 프롬프트입니다.'}
              {activePromptKey === 'query_expansion_prompt' && '짧은 질문을 검색에 최적화된 형태로 확장하는 쿼리 확장 프롬프트입니다.'}
              {activePromptKey === 'user_prompt_template' && (
                <>유저 프롬프트 템플릿입니다. 변수: <code className="bg-gray-200 px-1 rounded">{'{context_text}'}</code>, <code className="bg-gray-200 px-1 rounded">{'{best_scenario}'}</code>, <code className="bg-gray-200 px-1 rounded">{'{normalized_query}'}</code></>
              )}
            </p>
          </div>

          {/* 프롬프트 편집 영역 */}
          <div className="flex-1 px-6 pb-6 pt-3 overflow-hidden flex flex-col">
            <textarea
              value={prompts[activePromptKey] || ''}
              onChange={(e) => setPrompts({...prompts, [activePromptKey]: e.target.value})}
              className="flex-1 w-full p-4 border border-gray-300 rounded-lg outline-none font-mono text-sm resize-none"
            />
          </div>

          <div className="p-4 border-t bg-gray-50 text-right">
            <button onClick={handlePromptSave} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-bold">전체 저장</button>
          </div>
        </div>
      )}

      {/* RAG 파이프라인 시각화 + 인라인 튜닝 (저장 시 일괄 반영) */}
      {activeTab === 'pipeline' && (
        <RagPipeline
          cfg={ragConfig}
          onSave={async (newCfg) => {
            setRagConfig(newCfg);
            await authFetch('/api/config', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(newCfg),
            });
            showAlert('RAG 설정이 저장되었습니다.');
          }}
        />
      )}

      {/* 3. RAG 설정 */}
      {activeTab === 'config' && (
        <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-bold text-lg text-gray-800">RAG 파라미터 설정</h3>
            <p className="text-sm text-gray-500 mt-1">답변 품질에 영향을 주는 핵심 변수들을 실시간으로 조정합니다.</p>
          </div>
          
          <div className="p-8 space-y-8 max-w-2xl">
            
            {/* Top K */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Top K (검색 문서 수): <span className="text-blue-600">{ragConfig.top_k}개</span>
              </label>
              <input 
                type="range" min="1" max="20" step="1"
                value={ragConfig.top_k}
                onChange={(e) => setRagConfig({...ragConfig, top_k: parseInt(e.target.value)})}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <p className="text-xs text-gray-500 mt-1">질문과 유사한 문서를 몇 개까지 가져올지 결정합니다. (높을수록 정보량 증가, 낮을수록 정확도 증가)</p>
            </div>

            {/* Threshold */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Distance Threshold (유사도 임계값): <span className="text-blue-600">{ragConfig.threshold}</span>
              </label>
              <input 
                type="range" min="0.5" max="2.0" step="0.1"
                value={ragConfig.threshold}
                onChange={(e) => setRagConfig({...ragConfig, threshold: parseFloat(e.target.value)})}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <p className="text-xs text-gray-500 mt-1">이 값보다 거리가 먼(관련성이 낮은) 문서는 답변 생성에 사용하지 않습니다. (낮을수록 엄격함)</p>
            </div>

            {/* Temperature */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Temperature (창의성): <span className="text-blue-600">{ragConfig.temperature}</span>
              </label>
              <input 
                type="range" min="0.0" max="1.0" step="0.1"
                value={ragConfig.temperature}
                onChange={(e) => setRagConfig({...ragConfig, temperature: parseFloat(e.target.value)})}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <p className="text-xs text-gray-500 mt-1">0에 가까울수록 사실 기반의 건조한 답변, 1에 가까울수록 창의적이고 다양한 답변을 생성합니다.</p>
            </div>

          </div>

          <div className="mt-auto p-4 border-t bg-gray-50 text-right">
            <button onClick={handleConfigSave} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-bold">
              설정 저장
            </button>
          </div>
        </div>
      )}

      {/* 4. 피드백 로그 */}
      {activeTab === 'feedback' && (
        <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col overflow-hidden">
          <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
            <h3 className="font-bold text-lg text-gray-800">답변 실패 피드백 로그</h3>
            <span className="text-sm text-gray-500">총 {feedbackList.length}건</span>
          </div>
          <div className="flex-1 overflow-y-auto p-0">
            <table className="w-full text-sm text-left text-gray-500">
              <thead className="text-xs text-gray-700 uppercase bg-gray-100 sticky top-0">
                <tr>
                  <th className="px-6 py-3 border-b w-28">날짜</th>
                  <th className="px-6 py-3 border-b w-24">유형</th>
                  <th className="px-6 py-3 border-b w-1/4">사용자 질문</th>
                  <th className="px-6 py-3 border-b w-1/4">챗봇 답변</th>
                  <th className="px-6 py-3 border-b w-1/4">피드백 내용</th>
                  <th className="px-6 py-3 border-b w-20 text-center">관리</th>
                </tr>
              </thead>
              <tbody>
                {feedbackList.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-400">
                      수집된 피드백 로그가 없습니다.
                    </td>
                  </tr>
                ) : (
                  feedbackList.map((log) => (
                    <tr key={log.id} className="bg-white border-b hover:bg-gray-50">
                      <td className="px-6 py-4 font-mono text-xs text-gray-600 align-top">{log.date}</td>
                      <td className="px-6 py-4 align-top">
                        <span className={`px-2 py-1 rounded text-xs font-bold inline-block text-center min-w-[80px] ${
                          log.type === 'user_report' ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {log.type === 'user_report' ? '사용자 신고' : '자동 감지'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-900 font-medium align-top">
                        <ExpandableText text={log.query} maxLength={60} />
                      </td>
                      <td className="px-6 py-4 text-gray-500 align-top">
                        <ExpandableText text={log.answer} maxLength={60} />
                      </td>
                      <td className="px-6 py-4 text-gray-500 align-top">
                        <ExpandableText text={log.comment} maxLength={60} />
                      </td>
                      <td className="px-6 py-4 text-center align-top">
                        <button 
                          onClick={() => handleDeleteFeedback(log.id)} 
                          className="text-red-500 hover:text-red-700 font-bold text-xs border border-red-200 bg-red-50 px-3 py-1 rounded hover:bg-red-100 transition-colors"
                        >
                          삭제
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 5. 지식 학습 (New) */}
      {activeTab === 'learn' && (
        <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-bold text-lg text-gray-800">새로운 지식 학습</h3>
            <p className="text-sm text-gray-500 mt-1">LLM이 참고할 수 있도록 새로운 텍스트 데이터를 벡터 DB에 추가합니다.</p>
          </div>
          
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="max-w-3xl space-y-6">
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">출처 (Source)</label>
                  <input 
                    type="text" 
                    value={learnSource}
                    onChange={(e) => setLearnSource(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="예: 관리자 직접 입력, 2024년 공지사항"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">카테고리 (Category)</label>
                  <input 
                    type="text" 
                    value={learnCategory}
                    onChange={(e) => setLearnCategory(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="예: General, 규정, 안내"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">질문 (Question)</label>
                <input 
                  type="text" 
                  value={learnQuestion}
                  onChange={(e) => setLearnQuestion(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md mb-4"
                  placeholder="예: 이 교육 과정의 수료 기준은 무엇인가요?"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">키워드 (Keywords)</label>
                <input 
                  type="text" 
                  value={learnKeywords}
                  onChange={(e) => setLearnKeywords(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md mb-4"
                  placeholder="예: 수료, 기준, 교육 (콤마로 구분)"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">학습할 내용 (Text Content / Answer)</label>
                <textarea 
                  value={learnText}
                  onChange={(e) => setLearnText(e.target.value)}
                  className="w-full h-64 p-4 border border-gray-300 rounded-lg outline-none resize-none"
                  placeholder="여기에 학습시킬 텍스트(답변)를 입력하세요..."
                />
              </div>

            </div>
          </div>

          <div className="p-4 border-t bg-gray-50 text-right">
            <button 
              onClick={handleLearn} 
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-bold"
            >
              학습 시키기
            </button>
          </div>
        </div>
      )}

      {/* 6. 컬렉션 관리 */}
      {activeTab === 'collection' && (
        <div className="flex-1 flex flex-col gap-4 overflow-y-auto">

          {/* 섹션 1: 컬렉션 목록 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
              <div>
                <h3 className="font-bold text-lg text-gray-800">컬렉션 목록</h3>
                <p className="text-xs text-gray-500 mt-0.5">활성 컬렉션이 실제 챗봇 답변에 사용됩니다.</p>
              </div>
              <button onClick={() => setIsCreateColOpen(true)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-bold text-sm">+ 새 컬렉션 생성</button>
            </div>
            <table className="w-full text-sm text-left text-gray-500">
              <thead className="text-xs text-gray-700 uppercase bg-gray-100">
                <tr>
                  <th className="px-6 py-3">컬렉션명</th>
                  <th className="px-6 py-3 text-center">문서수</th>
                  <th className="px-6 py-3 text-center">상태</th>
                  <th className="px-6 py-3">생성일</th>
                  <th className="px-6 py-3 text-center">관리</th>
                </tr>
              </thead>
              <tbody>
                {collectionData.collections.length === 0 ? (
                  <tr><td colSpan="5" className="px-6 py-6 text-center text-gray-400">컬렉션이 없습니다.</td></tr>
                ) : collectionData.collections.map(col => (
                  <tr key={col.name} className="bg-white border-b hover:bg-gray-50">
                    <td className="px-6 py-3 font-mono font-bold text-gray-800">{col.name}</td>
                    <td className="px-6 py-3 text-center">{col.doc_count.toLocaleString()}</td>
                    <td className="px-6 py-3 text-center">
                      {col.is_active
                        ? <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-bold">활성</span>
                        : <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded text-xs">대기</span>}
                    </td>
                    <td className="px-6 py-3 text-xs text-gray-500">{col.created_at}</td>
                    <td className="px-6 py-3 text-center flex gap-2 justify-center">
                      {!col.is_active && (
                        <>
                          <button onClick={() => handleActivateCollection(col.name)} className="text-xs px-3 py-1 bg-blue-50 text-blue-600 border border-blue-200 rounded hover:bg-blue-100">활성화</button>
                          <button onClick={() => handleDeleteCollection(col.name)} className="text-xs px-3 py-1 bg-red-50 text-red-500 border border-red-200 rounded hover:bg-red-100">삭제</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 섹션 2: 데이터 임베딩 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b bg-gray-50">
              <h3 className="font-bold text-lg text-gray-800">데이터 임베딩</h3>
              <p className="text-xs text-gray-500 mt-0.5">파일을 선택한 컬렉션에 임베딩합니다. (PDF · HWP · HWPX · JSON)</p>
            </div>
            <div className="p-6 space-y-4 max-w-3xl">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">대상 컬렉션</label>
                  <select value={embedTarget} onChange={e => { setEmbedTarget(e.target.value); fetchEmbeddedFiles(e.target.value); }} className="w-full p-2 border border-gray-300 rounded-md text-sm">
                    {collectionData.collections.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">교육 (카테고리)</label>
                  <select value={embedCategory} onChange={e => setEmbedCategory(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md text-sm">
                    <option value="">교육을 선택하세요</option>
                    {embedEducations.map(edu => <option key={edu.id} value={edu.name}>{edu.name}</option>)}
                  </select>
                  {embedEducations.length === 0 && (
                    <p className="text-xs text-red-400 mt-1">먼저 '교육 관리' 탭에서 교육을 등록하세요.</p>
                  )}
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-bold text-gray-700 mb-1">키워드 <span className="font-normal text-gray-400">(쉼표로 구분, BM25 검색에 활용)</span></label>
                  <input type="text" value={embedKeywords} onChange={e => setEmbedKeywords(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md text-sm" placeholder="예: 체외진단, 교육일정, 의료기기" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">청크 전략</label>
                <div className="flex gap-4 flex-wrap">
                  {[
                    { value: 'paragraph', label: '단락 분할',  desc: '빈 줄 기준 (PDF/HWP 권장)' },
                    { value: 'fixed',     label: '고정 크기',  desc: '1000자 + 150자 오버랩' },
                    { value: 'qa_extract',label: 'Q&A 추출',   desc: '상담/고객 페어 (JSON STT)' },
                    { value: 'sentence',  label: '문장 분할',  desc: '마침표 기준' },
                  ].map(s => {
                    return (
                      <label key={s.value} className="flex items-start gap-2 cursor-pointer">
                        <input type="radio" name="strategy" value={s.value} checked={embedStrategy === s.value} onChange={() => setEmbedStrategy(s.value)} className="mt-0.5" />
                        <span>
                          <span className="text-sm font-bold text-gray-700">{s.label}</span>
                          <span className="block text-xs text-gray-400">{s.desc}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">파일 선택</label>
                <label
                  className={`flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-lg cursor-pointer transition-all ${isDragOver || embedFile ? 'border-blue-500 bg-blue-50 shadow-[0_0_0_3px_rgba(59,130,246,0.25)]' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}
                  onDragEnter={e => { e.preventDefault(); setIsDragOver(true); }}
                  onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
                  onDragLeave={e => { e.preventDefault(); setIsDragOver(false); }}
                  onDrop={e => {
                    e.preventDefault();
                    setIsDragOver(false);
                    const f = e.dataTransfer.files[0];
                    if (f) {
                      setEmbedFile(f);
                      setEmbedResult(null);
                      if (f.name.endsWith('.json')) setEmbedStrategy('qa_extract');
                      else setEmbedStrategy('paragraph');
                    }
                  }}
                >
                  <span className="text-gray-400 text-sm">{embedFile ? `${embedFile.name}` : '파일을 여기에 드래그하거나 클릭하세요'}</span>
                  <span className="text-gray-300 text-xs mt-1">PDF · HWP · HWPX · JSON</span>
                  <input type="file" accept=".pdf,.hwp,.hwpx,.json" className="hidden"
                    onChange={e => {
                      const f = e.target.files[0];
                      if (f) {
                        setEmbedFile(f);
                        setEmbedResult(null);
                        if (f.name.endsWith('.json')) setEmbedStrategy('qa_extract');
                        else setEmbedStrategy('paragraph');
                      }
                    }}
                  />
                </label>
              </div>

              {/* 인라인 파싱 미리보기 */}
              {(previewLoading || previewData || previewError) && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-gray-50 border-b flex items-center justify-between">
                    <span className="text-xs font-bold text-gray-600">파싱 미리보기</span>
                    {previewData && <span className="text-xs text-gray-400">총 {previewData.total_chunks}개 청크</span>}
                  </div>
                  {previewLoading && (
                    <div className="py-6 text-center text-gray-400 text-xs">파싱 중...</div>
                  )}
                  {previewError && (
                    <div className="p-3 text-red-600 text-xs bg-red-50">{previewError}</div>
                  )}
                  {previewData && !previewLoading && (
                    <>
                      <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
                        {previewData.chunks
                          .slice((previewPage - 1) * PREVIEW_PAGE_SIZE, previewPage * PREVIEW_PAGE_SIZE)
                          .map(chunk => (
                            <div key={chunk.index} className="p-3 hover:bg-gray-50">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-bold text-gray-400">#{chunk.index}</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded font-bold ${
                                  chunk.type === 'table' ? 'bg-blue-100 text-blue-700' :
                                  chunk.type === 'qa_pairs' ? 'bg-purple-100 text-purple-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>{chunk.type}</span>
                                <span className="text-xs text-gray-400 ml-auto">{chunk.char_count}자</span>
                              </div>
                              <p className={`text-xs text-gray-700 whitespace-pre-wrap font-mono leading-relaxed ${expandedChunks.has(chunk.index) ? '' : 'line-clamp-4'}`}>
                                {chunk.content}
                              </p>
                              <button
                                onClick={() => setExpandedChunks(prev => {
                                  const next = new Set(prev);
                                  next.has(chunk.index) ? next.delete(chunk.index) : next.add(chunk.index);
                                  return next;
                                })}
                                className="mt-1 text-xs text-blue-500 hover:text-blue-700"
                              >
                                {expandedChunks.has(chunk.index) ? '접기 ▲' : '더보기 ▼'}
                              </button>
                            </div>
                          ))}
                      </div>
                      {previewData.total_chunks > PREVIEW_PAGE_SIZE && (
                        <div className="flex justify-center gap-2 p-2 border-t bg-gray-50">
                          <button
                            onClick={() => setPreviewPage(p => Math.max(1, p - 1))}
                            disabled={previewPage === 1}
                            className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
                          >이전</button>
                          <span className="text-xs text-gray-500 self-center">
                            {previewPage} / {Math.ceil(previewData.total_chunks / PREVIEW_PAGE_SIZE)}
                          </span>
                          <button
                            onClick={() => setPreviewPage(p => Math.min(Math.ceil(previewData.total_chunks / PREVIEW_PAGE_SIZE), p + 1))}
                            disabled={previewPage >= Math.ceil(previewData.total_chunks / PREVIEW_PAGE_SIZE)}
                            className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
                          >다음</button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={handleEmbedFile}
                  disabled={embedLoading || !embedFile}
                  className={`px-6 py-2 rounded-md font-bold text-sm text-white transition-colors ${embedLoading || !embedFile ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                >
                  {embedLoading ? '임베딩 중...' : '임베딩 시작'}
                </button>
              </div>

              {embedLoading && (
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>임베딩 진행 중...</span>
                    <span>{embedProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden border border-gray-300">
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{
                        width: `${embedProgress}%`,
                        background: embedProgress < 50
                          ? 'linear-gradient(90deg, #3b82f6, #60a5fa)'
                          : embedProgress < 80
                          ? 'linear-gradient(90deg, #3b82f6, #22c55e)'
                          : 'linear-gradient(90deg, #16a34a, #4ade80)',
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1 text-right">{embedProgress < 100 ? `청크 처리 중...` : '완료'}</p>
                </div>
              )}

              {embedResult && (
                <div className={`p-3 rounded-lg text-sm ${embedResult.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                  {embedResult.success
                    ? `완료: ${embedResult.filename} → ${embedResult.chunk_count}개 청크 완료${embedResult.errors > 0 ? ` (오류 ${embedResult.errors}건)` : ''}`
                    : `실패: ${embedResult.message}`}
                </div>
              )}

            </div>
          </div>

          {/* 섹션 3: 임베딩 파일 목록 */}
          {embedTarget && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                <h3 className="font-bold text-lg text-gray-800">{embedTarget} — 임베딩 파일 목록</h3>
                <span className="text-sm text-gray-500">{embeddedFiles.length}개 파일</span>
              </div>
              <table className="w-full text-sm text-left text-gray-500">
                <thead className="text-xs text-gray-700 uppercase bg-gray-100">
                  <tr>
                    <th className="px-6 py-3">파일명</th>
                    <th className="px-6 py-3 text-center">청크수</th>
                    <th className="px-6 py-3">임베딩일</th>
                    <th className="px-6 py-3 text-center">관리</th>
                  </tr>
                </thead>
                <tbody>
                  {embeddedFiles.length === 0 ? (
                    <tr><td colSpan="4" className="px-6 py-6 text-center text-gray-400">임베딩된 파일이 없습니다.</td></tr>
                  ) : embeddedFiles.map(f => (
                    <React.Fragment key={f.filename}>
                      <tr className="bg-white border-b hover:bg-gray-50">
                        <td className="px-6 py-3 font-mono text-gray-800">
                          <button
                            onClick={() => handleToggleChunks(f.filename)}
                            className="text-left hover:text-blue-600 transition-colors"
                          >
                            <span className="mr-2 text-gray-400 text-xs">{expandedFile === f.filename ? '▲' : '▶'}</span>
                            {f.filename}
                          </button>
                        </td>
                        <td className="px-6 py-3 text-center">{f.chunk_count}</td>
                        <td className="px-6 py-3 text-xs text-gray-500">{f.embedded_at}</td>
                        <td className="px-6 py-3 text-center">
                          <button onClick={() => handleDeleteEmbeddedFile(embedTarget, f.filename)} className="text-xs px-3 py-1 bg-red-50 text-red-500 border border-red-200 rounded hover:bg-red-100">삭제</button>
                        </td>
                      </tr>
                      {expandedFile === f.filename && (
                        <tr>
                          <td colSpan="4" className="px-0 py-0 bg-gray-50 border-b">
                            <div className="p-4">
                              {fileChunksLoading ? (
                                <p className="text-sm text-gray-400 text-center py-4">불러오는 중...</p>
                              ) : (
                                <>
                                  <div className="flex justify-between items-center mb-2">
                                    <span className="text-xs text-gray-500">전체 {fileChunksData.total}개 청크</span>
                                    <span className="text-xs text-gray-400">{fileChunksData.page} / {fileChunksData.pages} 페이지</span>
                                  </div>
                                  <div className="border border-gray-200 rounded-lg overflow-hidden mb-2">
                                    <table className="w-full text-xs text-left">
                                      <thead className="bg-gray-100 text-gray-500">
                                        <tr>
                                          <th className="px-3 py-2 w-16">#</th>
                                          <th className="px-3 py-2">청크 내용</th>
                                          <th className="px-3 py-2 w-24">카테고리</th>
                                          <th className="px-3 py-2 w-16 text-center">삭제</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-100">
                                        {fileChunksData.chunks.map((c, i) => (
                                          <tr key={c.id} className="bg-white hover:bg-gray-50">
                                            <td className="px-3 py-2 text-gray-400">{(fileChunksData.page - 1) * 20 + i + 1}</td>
                                            <td className="px-3 py-2 text-gray-700">
                                              <ExpandableText text={c.chunk_data} maxLength={120} />
                                            </td>
                                            <td className="px-3 py-2">
                                              <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{c.category || '-'}</span>
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                              <button
                                                onClick={() => handleDeleteChunk(c.id, f.filename)}
                                                className="text-xs px-2 py-1 bg-red-50 text-red-400 border border-red-200 rounded hover:bg-red-100"
                                              >삭제</button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                  <div className="flex justify-center gap-2">
                                    <button
                                      onClick={() => { const p = fileChunksData.page - 1; fetchFileChunks(embedTarget, f.filename, p); }}
                                      disabled={fileChunksData.page <= 1}
                                      className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40"
                                    >이전</button>
                                    <button
                                      onClick={() => { const p = fileChunksData.page + 1; fetchFileChunks(embedTarget, f.filename, p); }}
                                      disabled={fileChunksData.page >= fileChunksData.pages}
                                      className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40"
                                    >다음</button>
                                  </div>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* 공통 데이터 관리: 모든 교육에서 자동 참조되는 전역 데이터 */}
      {activeTab === 'common' && (
        <div className="flex-1 flex flex-col gap-4 overflow-y-auto">

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-bold text-lg text-blue-800">공통 데이터 관리</h3>
            <p className="text-sm text-blue-700 mt-1">
              여기에 등록한 데이터는 <b>어떤 교육을 선택하든 항상 함께 검색</b>됩니다.
              모든 교육에 공통으로 적용할 내용을 한 곳에서 관리하세요.
            </p>
          </div>

          {/* 섹션 1: 공통 데이터 임베딩 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b bg-gray-50">
              <h3 className="font-bold text-lg text-gray-800">공통 데이터 임베딩</h3>
              <p className="text-xs text-gray-500 mt-0.5">파일을 공통 데이터에 임베딩합니다. (PDF · HWP · HWPX · JSON)</p>
            </div>
            <div className="p-6 space-y-4 max-w-2xl">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">청크 전략</label>
                <div className="flex gap-4 flex-wrap">
                  {[
                    { value: 'paragraph', label: '단락 분할', desc: '빈 줄 기준 (PDF/HWP 권장)' },
                    { value: 'fixed',     label: '고정 크기', desc: '1000자 + 150자 오버랩' },
                    { value: 'qa_extract',label: 'Q&A 추출',  desc: '상담/고객 페어 (JSON STT)' },
                    { value: 'sentence',  label: '문장 분할', desc: '마침표 기준' },
                  ].map(s => (
                    <label key={s.value} className="flex items-start gap-2 cursor-pointer">
                      <input type="radio" name="common-strategy" value={s.value} checked={commonStrategy === s.value} onChange={() => setCommonStrategy(s.value)} className="mt-0.5" />
                      <span>
                        <span className="text-sm font-bold text-gray-700">{s.label}</span>
                        <span className="block text-xs text-gray-400">{s.desc}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">파일 선택</label>
                <label
                  className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => {
                    e.preventDefault();
                    const f = e.dataTransfer.files[0];
                    if (f) {
                      setCommonFile(f);
                      setCommonResult(null);
                      if (f.name.endsWith('.json')) setCommonStrategy('qa_extract');
                      else setCommonStrategy('paragraph');
                    }
                  }}
                >
                  <span className="text-gray-400 text-sm">{commonFile ? `${commonFile.name}` : '파일을 여기에 드래그하거나 클릭하세요'}</span>
                  <span className="text-gray-300 text-xs mt-1">PDF · HWP · HWPX · JSON</span>
                  <input type="file" accept=".pdf,.hwp,.hwpx,.json" className="hidden"
                    onChange={e => {
                      const f = e.target.files[0];
                      if (f) {
                        setCommonFile(f);
                        setCommonResult(null);
                        if (f.name.endsWith('.json')) setCommonStrategy('qa_extract');
                        else setCommonStrategy('paragraph');
                      }
                    }}
                  />
                </label>
              </div>

              {commonLoading && (
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>임베딩 진행 중...</span>
                    <span>{commonProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden border border-gray-300">
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{
                        width: `${commonProgress}%`,
                        background: commonProgress < 50
                          ? 'linear-gradient(90deg, #3b82f6, #60a5fa)'
                          : commonProgress < 80
                          ? 'linear-gradient(90deg, #3b82f6, #22c55e)'
                          : 'linear-gradient(90deg, #16a34a, #4ade80)',
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1 text-right">{commonProgress < 100 ? `청크 처리 중...` : '완료'}</p>
                </div>
              )}

              {commonResult && (
                <div className={`p-3 rounded-lg text-sm ${commonResult.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                  {commonResult.success
                    ? `완료: ${commonResult.filename} → ${commonResult.chunk_count}개 청크 완료${commonResult.errors > 0 ? ` (오류 ${commonResult.errors}건)` : ''}`
                    : `실패: ${commonResult.message}`}
                </div>
              )}

              <div className="text-right">
                <button onClick={handleCommonEmbed} disabled={commonLoading || !commonFile} className={`px-6 py-2 rounded-md font-bold text-sm text-white transition-colors ${commonLoading || !commonFile ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}>
                  {commonLoading ? '임베딩 중...' : '임베딩 시작'}
                </button>
              </div>
            </div>
          </div>

          {/* 섹션 2: 공통 데이터 파일 목록 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
              <h3 className="font-bold text-lg text-gray-800">공통 데이터 — 임베딩 파일 목록</h3>
              <span className="text-sm text-gray-500">{commonFiles.length}개 파일</span>
            </div>
            <table className="w-full text-sm text-left text-gray-500">
              <thead className="text-xs text-gray-700 uppercase bg-gray-100">
                <tr>
                  <th className="px-6 py-3">파일명</th>
                  <th className="px-6 py-3 text-center">청크수</th>
                  <th className="px-6 py-3">임베딩일</th>
                  <th className="px-6 py-3 text-center">관리</th>
                </tr>
              </thead>
              <tbody>
                {commonFiles.length === 0 ? (
                  <tr><td colSpan="4" className="px-6 py-6 text-center text-gray-400">임베딩된 파일이 없습니다.</td></tr>
                ) : commonFiles.map(f => (
                  <React.Fragment key={f.filename}>
                    <tr className="bg-white border-b hover:bg-gray-50">
                      <td className="px-6 py-3 font-mono text-gray-800">
                        <button
                          onClick={() => handleToggleChunks(f.filename, COMMON_COLLECTION)}
                          className="text-left hover:text-blue-600 transition-colors"
                        >
                          <span className="mr-2 text-gray-400 text-xs">{expandedFile === f.filename ? '▲' : '▶'}</span>
                          {f.filename}
                        </button>
                      </td>
                      <td className="px-6 py-3 text-center">{f.chunk_count}</td>
                      <td className="px-6 py-3 text-xs text-gray-500">{f.embedded_at}</td>
                      <td className="px-6 py-3 text-center">
                        <button onClick={() => handleDeleteCommonFile(f.filename)} className="text-xs px-3 py-1 bg-red-50 text-red-500 border border-red-200 rounded hover:bg-red-100">삭제</button>
                      </td>
                    </tr>
                    {expandedFile === f.filename && (
                      <tr>
                        <td colSpan="4" className="px-0 py-0 bg-gray-50 border-b">
                          <div className="p-4">
                            {fileChunksLoading ? (
                              <p className="text-sm text-gray-400 text-center py-4">불러오는 중...</p>
                            ) : (
                              <>
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-xs text-gray-500">전체 {fileChunksData.total}개 청크</span>
                                  <span className="text-xs text-gray-400">{fileChunksData.page} / {fileChunksData.pages} 페이지</span>
                                </div>
                                <div className="border border-gray-200 rounded-lg overflow-hidden mb-2">
                                  <table className="w-full text-xs text-left">
                                    <thead className="bg-gray-100 text-gray-500">
                                      <tr>
                                        <th className="px-3 py-2 w-16">#</th>
                                        <th className="px-3 py-2">청크 내용</th>
                                        <th className="px-3 py-2 w-16 text-center">삭제</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                      {fileChunksData.chunks.map((c, i) => (
                                        <tr key={c.id} className="bg-white hover:bg-gray-50">
                                          <td className="px-3 py-2 text-gray-400">{(fileChunksData.page - 1) * 20 + i + 1}</td>
                                          <td className="px-3 py-2 text-gray-700">
                                            <ExpandableText text={c.chunk_data} maxLength={120} />
                                          </td>
                                          <td className="px-3 py-2 text-center">
                                            <button
                                              onClick={() => handleDeleteChunk(c.id, f.filename, COMMON_COLLECTION)}
                                              className="text-xs px-2 py-1 bg-red-50 text-red-400 border border-red-200 rounded hover:bg-red-100"
                                            >삭제</button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                <div className="flex justify-center gap-2">
                                  <button
                                    onClick={() => { const p = fileChunksData.page - 1; fetchFileChunks(COMMON_COLLECTION, f.filename, p); }}
                                    disabled={fileChunksData.page <= 1}
                                    className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40"
                                  >이전</button>
                                  <button
                                    onClick={() => { const p = fileChunksData.page + 1; fetchFileChunks(COMMON_COLLECTION, f.filename, p); }}
                                    disabled={fileChunksData.page >= fileChunksData.pages}
                                    className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40"
                                  >다음</button>
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 8. 테마 설정 */}
      {activeTab === 'education' && (
        <div className="flex-1 flex flex-col gap-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
              <div>
                <h3 className="font-bold text-lg text-gray-800">교육 관리</h3>
                <p className="text-xs text-gray-500 mt-0.5">교육을 등록하면 데이터 임베딩 시 교육을 선택할 수 있고, 챗봇은 선택된 교육의 데이터만 사용해 답변합니다.</p>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 mr-2">대상 컬렉션</label>
                <select value={eduCollection} onChange={e => setEduCollection(e.target.value)} className="p-2 border border-gray-300 rounded-md text-sm">
                  {collectionData.collections.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                </select>
              </div>
            </div>

            {/* 교육 추가 폼 */}
            <div className="p-4 border-b flex gap-3 items-end bg-white">
              <div className="flex-1">
                <label className="block text-xs font-bold text-gray-500 mb-1">교육명</label>
                <input type="text" value={newEduName} onChange={e => setNewEduName(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md text-sm" placeholder="예: 신입사원 교육, 안전 교육" />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-bold text-gray-500 mb-1">설명 (선택)</label>
                <input type="text" value={newEduDesc} onChange={e => setNewEduDesc(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md text-sm" placeholder="교육 설명" />
              </div>
              <button onClick={handleCreateEducation} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-bold text-sm whitespace-nowrap">+ 추가</button>
            </div>

            {/* 교육 목록 */}
            <table className="w-full text-sm text-left text-gray-500">
              <thead className="text-xs text-gray-700 uppercase bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-center">순서</th>
                  <th className="px-6 py-3">교육명</th>
                  <th className="px-6 py-3">설명</th>
                  <th className="px-6 py-3">생성일</th>
                  <th className="px-6 py-3 text-center">관리</th>
                </tr>
              </thead>
              <tbody>
                {educations.length === 0 ? (
                  <tr><td colSpan="5" className="px-6 py-6 text-center text-gray-400">등록된 교육이 없습니다.</td></tr>
                ) : educations.map((edu, idx) => (
                  <tr key={edu.id} className="bg-white border-b hover:bg-gray-50">
                    <td className="px-6 py-3 text-center whitespace-nowrap">
                      <button onClick={() => handleMoveEducation(idx, -1)} disabled={idx === 0}
                              className={`px-1.5 ${idx === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:text-blue-600'}`}
                              title="위로">▲</button>
                      <button onClick={() => handleMoveEducation(idx, 1)} disabled={idx === educations.length - 1}
                              className={`px-1.5 ${idx === educations.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:text-blue-600'}`}
                              title="아래로">▼</button>
                    </td>
                    <td className="px-6 py-3 font-bold text-gray-800">{edu.name}</td>
                    <td className="px-6 py-3 text-gray-500">{edu.description}</td>
                    <td className="px-6 py-3 text-xs text-gray-500">{edu.created_at}</td>
                    <td className="px-6 py-3 text-center">
                      <button onClick={() => handleDeleteEducation(edu.id, edu.name)} className="text-xs px-3 py-1 bg-red-50 text-red-500 border border-red-200 rounded hover:bg-red-100">삭제</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'theme' && (
        <div className="flex flex-col gap-6 overflow-y-auto flex-1 max-w-2xl">

          {/* 인사말 */}
          <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-sm font-bold text-gray-700 mb-3">챗봇 첫 인사말</h3>
            <textarea
              value={themeData.greeting}
              onChange={e => setThemeData(prev => ({ ...prev, greeting: e.target.value }))}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-blue-400 resize-none"
              placeholder="챗봇이 처음 표시할 인사말을 입력하세요."
            />
          </div>

          {/* 주요 색상 */}
          <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-sm font-bold text-gray-700 mb-3">UI 주요 색상</h3>
            <div className="flex items-center gap-4">
              <input
                type="color"
                value={themeData.primary_color}
                onChange={e => setThemeData(prev => ({ ...prev, primary_color: e.target.value }))}
                className="w-12 h-10 rounded cursor-pointer border border-gray-300"
              />
              <span className="text-sm text-gray-600 font-mono">{themeData.primary_color}</span>
              <div className="flex gap-2 ml-2">
                {['#1A73E8', '#0F9D58', '#F4B400', '#DB4437', '#7B1FA2', '#00796B', '#E64A19'].map(c => (
                  <button
                    key={c}
                    onClick={() => setThemeData(prev => ({ ...prev, primary_color: c }))}
                    className="w-7 h-7 rounded-full border-2 border-white shadow"
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">상단 네비게이션 바, 버튼, 활성 탭 색상에 적용됩니다.</p>
          </div>

          {/* 로고 */}
          <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-sm font-bold text-gray-700 mb-3">좌상단 로고</h3>
            <div className="flex items-center gap-4 mb-3">
              {themeLogoPreview ? (
                <img src={themeLogoPreview} alt="현재 로고" className="h-12 w-auto object-contain border border-gray-200 rounded p-1 bg-gray-800" />
              ) : (
                <div className="h-12 w-32 flex items-center justify-center border border-dashed border-gray-300 rounded text-xs text-gray-400">기본 로고 사용 중</div>
              )}
              {themeLogoPreview && (
                <button onClick={handleDeleteLogo} className="text-xs px-3 py-1 bg-red-50 text-red-500 border border-red-200 rounded hover:bg-red-100">기본값으로</button>
              )}
            </div>
            <label className="flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
              onDragOver={e => e.preventDefault()}
              onDrop={e => {
                e.preventDefault();
                const f = e.dataTransfer.files[0];
                if (f) { setThemeLogoFile(f); setThemeLogoPreview(URL.createObjectURL(f)); }
              }}
            >
              <span className="text-gray-400 text-sm">{themeLogoFile ? themeLogoFile.name : '로고 파일을 드래그하거나 클릭하세요'}</span>
              <span className="text-gray-300 text-xs mt-1">PNG · JPG · WEBP</span>
              <input type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden"
                onChange={e => {
                  const f = e.target.files[0];
                  if (f) { setThemeLogoFile(f); setThemeLogoPreview(URL.createObjectURL(f)); }
                }}
              />
            </label>
            <p className="text-xs text-gray-400 mt-2">배경이 흰색인 로고는 네이게이션 배경색 위에서 반전 처리됩니다.</p>
          </div>

          {/* 챗봇 프로필 이미지 */}
          <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-sm font-bold text-gray-700 mb-3">챗봇 프로필 이미지</h3>
            <div className="flex items-center gap-4 mb-3">
              {themeProfilePreview ? (
                <img src={themeProfilePreview} alt="현재 프로필" className="h-12 w-12 object-cover rounded-full border border-gray-200" />
              ) : (
                <div className="h-12 w-12 flex items-center justify-center border border-dashed border-gray-300 rounded-full text-[10px] text-gray-400 text-center">기본<br/>아이콘</div>
              )}
              {themeProfilePreview && (
                <button onClick={handleDeleteProfile} className="text-xs px-3 py-1 bg-red-50 text-red-500 border border-red-200 rounded hover:bg-red-100">기본값으로</button>
              )}
            </div>
            <label className="flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
              onDragOver={e => e.preventDefault()}
              onDrop={e => {
                e.preventDefault();
                const f = e.dataTransfer.files[0];
                if (f) { setThemeProfileFile(f); setThemeProfilePreview(URL.createObjectURL(f)); }
              }}
            >
              <span className="text-gray-400 text-sm">{themeProfileFile ? themeProfileFile.name : '프로필 이미지를 드래그하거나 클릭하세요'}</span>
              <span className="text-gray-300 text-xs mt-1">PNG · JPG · WEBP · 정사각형 권장</span>
              <input type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden"
                onChange={e => {
                  const f = e.target.files[0];
                  if (f) { setThemeProfileFile(f); setThemeProfilePreview(URL.createObjectURL(f)); }
                }}
              />
            </label>
            <p className="text-xs text-gray-400 mt-2">채팅 화면의 챗봇 아바타로 사용됩니다. (원형으로 표시)</p>
          </div>

          <button
            onClick={handleSaveTheme}
            disabled={themeSaving}
            className="px-6 py-2.5 bg-main-point text-white rounded-lg text-sm font-bold hover:opacity-90 disabled:opacity-50 self-start"
          >
            {themeSaving ? '저장 중...' : '테마 저장'}
          </button>

        </div>
      )}

      {/* 7. 플레이그라운드 */}
      {activeTab === 'test' && (
        <div className="flex flex-col gap-6 overflow-y-auto flex-1">

          {/* RAG 테스트 플레이그라운드 섹션 */}
          <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-800">RAG 테스트 플레이그라운드</h3>
              <div className="flex items-center gap-3">
                <select
                  value={testCollection}
                  onChange={e => setTestCollection(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                >
                  {collectionData.collections.map(c => (
                    <option key={c.name} value={c.name}>{c.name}{c.is_active ? ' (활성)' : ''}</option>
                  ))}
                </select>
                <button
                  onClick={handleTestQuery}
                  disabled={testLoading}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50"
                >
                  {testLoading ? '실행 중...' : '검색 테스트 실행'}
                </button>
              </div>
            </div>

            <div className="flex gap-6 items-stretch">
              {/* 좌측: 파라미터 */}
              <div className="w-64 flex-shrink-0 flex flex-col">

                {/* 매개 변수 */}
                <div className="border border-gray-200 rounded-lg p-3 flex flex-col gap-4 flex-1">
                  <p className="text-xs font-bold text-gray-600">매개 변수</p>

                  {/* Top K */}
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="text-xs text-gray-600">Top K</label>
                      <input
                        type="number" min={1} max={20}
                        value={playgroundTopK}
                        onChange={e => setPlaygroundTopK(Math.min(20, Math.max(1, parseInt(e.target.value) || 1)))}
                        className="w-14 border border-gray-300 rounded px-2 py-0.5 text-xs text-center focus:outline-none focus:border-blue-400"
                      />
                    </div>
                    <input type="range" min={1} max={20} step={1}
                      value={playgroundTopK}
                      onChange={e => setPlaygroundTopK(parseInt(e.target.value))}
                      className="w-full accent-blue-500"
                    />
                  </div>

                  {/* Temperature */}
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="text-xs text-gray-600">Temperature</label>
                      <input
                        type="number" min={0} max={2} step={0.1}
                        value={playgroundTemperature}
                        onChange={e => setPlaygroundTemperature(Math.min(2, Math.max(0, parseFloat(e.target.value) || 0)))}
                        className="w-14 border border-gray-300 rounded px-2 py-0.5 text-xs text-center focus:outline-none focus:border-blue-400"
                      />
                    </div>
                    <input type="range" min={0} max={2} step={0.1}
                      value={playgroundTemperature}
                      onChange={e => setPlaygroundTemperature(parseFloat(e.target.value))}
                      className="w-full accent-blue-500"
                    />
                  </div>

                  {/* Max Tokens */}
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="text-xs text-gray-600">최대 토큰 수</label>
                      <input
                        type="number" min={256} max={4096} step={64}
                        value={playgroundMaxTokens}
                        onChange={e => setPlaygroundMaxTokens(Math.min(4096, Math.max(256, parseInt(e.target.value) || 256)))}
                        className="w-16 border border-gray-300 rounded px-2 py-0.5 text-xs text-center focus:outline-none focus:border-blue-400"
                      />
                    </div>
                    <input type="range" min={256} max={4096} step={64}
                      value={playgroundMaxTokens}
                      onChange={e => setPlaygroundMaxTokens(parseInt(e.target.value))}
                      className="w-full accent-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* 우측: 질문 입력 */}
              <div className="flex-1 flex flex-col">
                <textarea
                  value={testQuery}
                  onChange={e => setTestQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleTestQuery(); }}
                  placeholder="테스트 질문을 입력하세요... (Ctrl+Enter로 실행)"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-blue-400 resize-none flex-1"
                  rows={6}
                />
              </div>
            </div>

            {testResult && (
              <>
                <div className="grid grid-cols-3 gap-4 mb-4 mt-6">
                  {/* 벡터 검색 결과 */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-blue-50 px-3 py-2 text-xs font-bold text-blue-700 border-b border-blue-100">
                      벡터 검색 결과 ({testResult.vector_results.length}건)
                    </div>
                    <div className="p-2 max-h-64 overflow-y-auto">
                      {testResult.vector_results.length === 0 ? (
                        <p className="text-xs text-gray-400 p-2">결과 없음</p>
                      ) : testResult.vector_results.map((r, i) => (
                        <div key={i} className="mb-2 p-2 bg-gray-50 rounded text-xs">
                          <div className="flex justify-between mb-1">
                            <span className="font-bold text-blue-600">유사도 {r.score}</span>
                            <span className="text-gray-400">{r.category}</span>
                          </div>
                          <p className="text-gray-500 truncate" title={r.document_name}>{r.document_name}</p>
                          <p className="text-gray-700 mt-1 line-clamp-2">{r.chunk_data}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* BM25 결과 */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-green-50 px-3 py-2 text-xs font-bold text-green-700 border-b border-green-100">
                      BM25 결과 ({testResult.bm25_results.length}건)
                      {!testResult.bm25_available && <span className="ml-1 text-gray-400 font-normal">(비활성 컬렉션)</span>}
                    </div>
                    <div className="p-2 max-h-64 overflow-y-auto">
                      {testResult.bm25_results.length === 0 ? (
                        <p className="text-xs text-gray-400 p-2">{testResult.bm25_available ? '결과 없음' : 'BM25는 활성 컬렉션에서만 동작합니다'}</p>
                      ) : testResult.bm25_results.map((r, i) => (
                        <div key={i} className="mb-2 p-2 bg-gray-50 rounded text-xs">
                          <div className="flex justify-between mb-1">
                            <span className="font-bold text-green-600">점수 {r.score}</span>
                            <span className="text-gray-400">{r.category}</span>
                          </div>
                          <p className="text-gray-500 truncate" title={r.document_name}>{r.document_name}</p>
                          <p className="text-gray-700 mt-1 line-clamp-2">{r.chunk_data}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 최종 답변 */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-purple-50 px-3 py-2 text-xs font-bold text-purple-700 border-b border-purple-100">
                      최종 답변 (GPT-4o)
                    </div>
                    <div className="p-3 max-h-64 overflow-y-auto">
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{testResult.answer}</p>
                    </div>
                  </div>
                </div>

                {/* 리랭크 결과 */}
                {testResult.reranked.length > 0 && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-orange-50 px-3 py-2 text-xs font-bold text-orange-700 border-b border-orange-100">
                      리랭크 후 최종 컨텍스트 ({testResult.reranked.length}건) — GPT에 전달된 문서
                    </div>
                    <div className="divide-y divide-gray-100">
                      {testResult.reranked.map((r, i) => (
                        <div key={i} className="px-3 py-2 text-xs">
                          <span className="font-bold text-orange-600 mr-2">#{i + 1}</span>
                          <span className="text-gray-500 mr-2">{r.document_name}</span>
                          <span className="bg-gray-100 text-gray-600 px-1 rounded">{r.category}</span>
                          <p className="text-gray-700 mt-1">{r.chunk_data}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-xs text-gray-400 mt-2">
                  정규화된 쿼리: <span className="font-mono text-gray-600">"{testResult.normalized_query}"</span>
                </p>
              </>
            )}
          </div>

          {/* 문서 브라우저 섹션 */}
          <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-base font-bold text-gray-800 mb-4">문서 브라우저</h3>

            <div className="flex gap-3 mb-4 flex-wrap">
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">컬렉션</label>
                <select
                  value={docBrowserCollection}
                  onChange={e => { setDocBrowserCollection(e.target.value); fetchFilterOptions(e.target.value); setFilterOptions({ categories: [], keywords: [] }); setDocBrowserKeyword(''); setDocBrowserCategory(''); }}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                >
                  {collectionData.collections.map(c => (
                    <option key={c.name} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1 min-w-32">
                <label className="block text-xs font-bold text-gray-500 mb-1">키워드</label>
                <select
                  value={docBrowserKeyword}
                  onChange={e => setDocBrowserKeyword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                >
                  <option value="">전체</option>
                  {filterOptions.keywords.map(kw => (
                    <option key={kw} value={kw}>{kw}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">카테고리</label>
                <select
                  value={docBrowserCategory}
                  onChange={e => setDocBrowserCategory(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                >
                  <option value="">전체</option>
                  {filterOptions.categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => { setDocBrowserPage(1); handleFetchDocBrowser(docBrowserCollection, docBrowserKeyword, docBrowserCategory, 1); }}
                  disabled={docBrowserLoading}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg text-sm font-bold hover:bg-gray-800 disabled:opacity-50"
                >
                  {docBrowserLoading ? '조회 중...' : '조회'}
                </button>
              </div>
            </div>

            {docBrowserData.documents.length > 0 && (
              <>
                <p className="text-xs text-gray-400 mb-2">
                  전체 {docBrowserData.total}건 | {docBrowserData.page}/{docBrowserData.pages} 페이지
                </p>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm text-left text-gray-600">
                    <thead className="bg-gray-50 text-xs text-gray-500 uppercase border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-2 w-40">파일명</th>
                        <th className="px-3 py-2 w-20">카테고리</th>
                        <th className="px-3 py-2">청크 내용</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {docBrowserData.documents.map((doc, i) => (
                        <tr key={doc.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-3 py-2 text-xs text-gray-500 break-all">{doc.document_name || '-'}</td>
                          <td className="px-3 py-2 text-xs">
                            <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs">{doc.category || '-'}</span>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-700">
                            <ExpandableText text={doc.chunk_data} maxLength={100} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* 페이지네이션 */}
                <div className="flex justify-center gap-2 mt-3">
                  <button
                    onClick={() => { const p = docBrowserPage - 1; setDocBrowserPage(p); handleFetchDocBrowser(docBrowserCollection, docBrowserKeyword, docBrowserCategory, p); }}
                    disabled={docBrowserPage <= 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40"
                  >이전</button>
                  <span className="px-3 py-1 text-sm text-gray-600">{docBrowserPage} / {docBrowserData.pages}</span>
                  <button
                    onClick={() => { const p = docBrowserPage + 1; setDocBrowserPage(p); handleFetchDocBrowser(docBrowserCollection, docBrowserKeyword, docBrowserCategory, p); }}
                    disabled={docBrowserPage >= docBrowserData.pages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40"
                  >다음</button>
                </div>
              </>
            )}

            {docBrowserData.documents.length === 0 && !docBrowserLoading && docBrowserData.total === 0 && (
              <p className="text-sm text-gray-400 text-center py-8">컬렉션을 선택하고 조회 버튼을 눌러주세요.</p>
            )}
          </div>

        </div>
      )}

      {/* 새 컬렉션 생성 모달 */}
      {isCreateColOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-lg font-bold mb-4 text-gray-800">새 컬렉션 생성</h3>
            <div className="mb-3">
              <label className="block text-sm font-bold mb-1 text-gray-700">컬렉션명 (영문)</label>
              <input type="text" value={createColName} onChange={e => setCreateColName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-blue-400"
                placeholder="예: kothea_v2 (영문/숫자/_/-만 가능)" />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-bold mb-1 text-gray-700">설명 (선택)</label>
              <input type="text" value={createColDesc} onChange={e => setCreateColDesc(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-blue-400"
                placeholder="예: 2026년 상반기 개정 데이터" />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setIsCreateColOpen(false); setCreateColName(''); setCreateColDesc(''); }} className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg text-sm">취소</button>
              <button onClick={handleCreateCollection} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700">생성</button>
            </div>
          </div>
        </div>
      )}

      {/* [추가] Alert Modal */}
      {alertState.isOpen && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-lg font-bold mb-4 text-gray-800">알림</h3>
            <p className="text-gray-600 mb-6">{alertState.message}</p>
            <div className="flex justify-end">
              <button 
                onClick={closeAlert}
                className="px-4 py-2 bg-main-point text-white rounded-lg text-sm font-bold hover:bg-blue-700"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* [추가] Confirm Modal */}
      {confirmState.isOpen && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-lg font-bold mb-4 text-gray-800">확인</h3>
            <p className="text-gray-600 mb-6">{confirmState.message}</p>
            <div className="flex justify-end gap-2">
              <button 
                onClick={closeConfirm}
                className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg text-sm"
              >
                취소
              </button>
              <button 
                onClick={handleConfirmAction}
                className="px-4 py-2 bg-main-point text-white rounded-lg text-sm font-bold hover:bg-blue-700"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminDashboard;
